import { useTranslations } from 'next-intl';
import { ExpandableText } from '@/components/common/ExpandableText/ExpandableText';
import { useAddressFormatter } from '@/hooks/useAddressFormatter';
import { JobDescriptionFragment, JobDetailFragment } from '../gen/graphql/JobRoute.generated';
import { useI18nLabel } from '@/hooks/useI18nLabel';
import { JobContentSection } from './JobContentSection';

export interface JobDescriptionProps {
  jobDescription: JobDescriptionFragment;
}

export function JobDescription({ jobDescription }: JobDescriptionProps) {
  const { formatAddress } = useAddressFormatter();
  const { description, benefits, notes, items, qualifications, address, access, smokingPolicy } =
    jobDescription;
  const t = useTranslations();
  const translate = useI18nLabel();

  return (
    <div className="w-full flex flex-col divide-y-thin divide-neutral-auxiliary-tinge">
      <JobContentSection emoji="📝" title={t('job.jobContentSection.description')}>
        <ExpandableText maxLines={7}>{description}</ExpandableText>
      </JobContentSection>

      {benefits && benefits.length > 0 && (
        <JobContentSection emoji="💎" title={t('job.jobContentSection.benefits')}>
          <ul className="flex flex-col gap-1 list-disc list-inside">
            {benefits?.map((benefit) => (
              <li key={benefit.id} className="text-small text-neutral-primary">
                {translate(benefit)}
              </li>
            ))}
          </ul>
        </JobContentSection>
      )}

      {notes && (
        <JobContentSection emoji="⚡" title={t('job.jobContentSection.notes')}>
          <div className="whitespace-pre-wrap text-small">{notes}</div>
        </JobContentSection>
      )}

      {items && items.length > 0 && (
        <JobContentSection emoji="🛍" title={t('job.jobContentSection.items')}>
          <ul className="flex flex-col gap-1 text-small list-disc list-inside">
            {items?.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </JobContentSection>
      )}

      {qualifications && qualifications.length > 0 && (
        <JobContentSection emoji="📋" title={t('job.jobContentSection.qualifications')}>
          <ul className="flex flex-col gap-1 text-small list-disc list-inside">
            {qualifications?.map((qualification) => (
              <li key={qualification}>{qualification}</li>
            ))}
          </ul>
        </JobContentSection>
      )}

      <JobContentSection emoji="🏢" title={t('job.jobContentSection.workplace')}>
        <div className="flex flex-col gap-6 font-small">
          <p className="text-small text-neutral-primary">{formatAddress(address)}</p>
          {process.env.NEXT_PUBLIC_MAPS_EMBED_API_KEY && (
            <iframe
              className="aspect-[16/9] w-full rounded-small"
              src={`https://maps.google.com/maps/embed/v1/place?${new URLSearchParams({
                key: process.env.NEXT_PUBLIC_MAPS_EMBED_API_KEY,
                q: formatAddress(address),
              })}`}
              title={formatAddress(address)}
              allowFullScreen
            ></iframe>
          )}
          {access && (
            <div className="rounded-medium p-6 bg-neutral-auxiliary-tinge whitespace-pre-wrap">
              {access}
            </div>
          )}
          {smokingPolicy && (
            <p className="text-x-small text-neutral-secondary">
              {t('job.smokingPolicy.format', {
                policy: translate(smokingPolicy),
              })}
            </p>
          )}
        </div>
      </JobContentSection>
    </div>
  );
}
